import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Save, Send, Plus, Copy, Loader2, AlertTriangle, Eye } from "lucide-react";
import { toast } from "sonner";
import { PersonCard, Field } from "@/components/PersonCard";
import { emptyPerson, errMsg } from "@/lib/api";
import { useApi } from "@/admin/AdminApp";

import StayEditor from "@/admin/StayEditor";


export default function FamilyDetail() {
  const { id } = useParams();
  const api = useApi();
  const nav = useNavigate();
  const [fam, setFam] = useState(null);
  const [fields, setFields] = useState([]);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    Promise.all([api("get", `/families/${id}`), api("get", "/fields")])
      .then(([f, fl]) => { setFam(f); setFields(fl); })
      .catch((e) => { toast.error(errMsg(e)); nav("/admin"); });
  }, [id, api, nav]);

  if (!fam) return <Loader2 className="mx-auto animate-spin text-[var(--gold)]" />;
  const set = (patch) => setFam({ ...fam, ...patch });
  const setPersons = (fn) => setFam((f) => ({ ...f, persons: fn(f.persons) }));
  const copyPrev = (i) => setPersons((ps) => ps.map((p, j) => (j === i ? {
    ...p, travel: { ...(ps[i - 1].travel || {}) },
    passport: { ...(p.passport || {}), nationality: ps[i - 1].passport?.nationality || "", visa_status: ps[i - 1].passport?.visa_status || "" },
  } : p)));

  const save = async () => {
    setBusy("save");
    try {
      const f = await api("put", `/families/${id}`, { name: fam.name, email: fam.email, mobile: fam.mobile, notes: fam.notes, rsvp: fam.rsvp, persons: fam.persons, stay: fam.stay || {} });
      setFam(f);
      toast.success("Saved");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const send = async (key) => {
    setBusy(key);
    try { await api("post", `/families/${id}/send/${key}`); toast.success("Email sent"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const link = `${window.location.origin}/f/${fam.link_token}`;
  const preview = async () => {
    const w = window.open("", "_blank");
    try {
      const { token } = await api("post", `/families/${id}/preview`);
      w.location.href = `${window.location.origin}/preview/${token}`;
    } catch (e) { w.close(); toast.error(errMsg(e)); }
  };

  return (
    <div className="mx-auto max-w-4xl pb-24" data-testid="family-detail-page">
      <button onClick={() => nav("/admin")} className="mb-6 inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[var(--ink)]" data-testid="back-to-families-btn"><ArrowLeft size={15} /> All families</button>
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="eyebrow">Code {fam.code}</div>
          <h1 className="font-serif-d mt-2 text-4xl sm:text-5xl" data-testid="family-detail-name">{fam.name}</h1>
          {fam.incomplete && <span className="mt-2 inline-flex items-center gap-1 rounded-full bg-amber-50 px-3 py-1 text-xs text-amber-800" data-testid="family-incomplete-badge"><AlertTriangle size={12} /> Incomplete registration</span>}
        </div>
        <div className="flex flex-wrap gap-2">
          <button className="btn-ghost !py-2 text-sm" onClick={preview} data-testid="detail-preview-guest-btn"><Eye size={14} /> Preview as guest</button>
          <button className="btn-ghost !py-2 text-sm" onClick={() => { navigator.clipboard.writeText(link); toast.success("Link copied"); }} data-testid="detail-copy-link-btn"><Copy size={14} /> Private link</button>
          <button className="btn-ghost !py-2 text-sm" onClick={() => send("invitation")} disabled={!!busy} data-testid="send-invitation-btn">{busy === "invitation" ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Invitation</button>
          <button className="btn-ghost !py-2 text-sm" onClick={() => send("stay")} disabled={!!busy} data-testid="send-stay-btn">{busy === "stay" ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Stay details</button>
        </div>
      </div>

      <div className="ivory-card mb-6 grid gap-4 p-6 sm:grid-cols-2">
        <Field label="Family name"><input className="input-lux" value={fam.name} onChange={(e) => set({ name: e.target.value })} data-testid="family-name-input" /></Field>
        <Field label="Response">
          <select className="input-lux" value={fam.rsvp} onChange={(e) => set({ rsvp: e.target.value })} data-testid="family-rsvp-select">
            <option value="pending">Not responded</option><option value="attending">Attending</option><option value="declined">Declined</option>
          </select>
        </Field>
        <Field label="Email"><input className="input-lux" value={fam.email} onChange={(e) => set({ email: e.target.value })} data-testid="family-email-input" /></Field>
        <Field label="Mobile"><input className="input-lux" value={fam.mobile} onChange={(e) => set({ mobile: e.target.value })} data-testid="family-mobile-input" /></Field>
        <Field label="Notes (organizers only)" className="sm:col-span-2"><textarea className="input-lux min-h-[70px]" value={fam.notes || ""} onChange={(e) => set({ notes: e.target.value })} data-testid="family-notes-input" /></Field>
        {fam.decline_message && <p className="text-sm text-slate-600 sm:col-span-2">Message from guest: “{fam.decline_message}”</p>}
      </div>

      <h2 className="font-serif-d mb-4 mt-10 text-3xl">Stay & transfers</h2>
      <div className="mb-6"><StayEditor stay={fam.stay} onChange={(stay) => set({ stay })} /></div>

      <h2 className="font-serif-d mb-4 mt-10 text-3xl">People</h2>
      <div className="space-y-5">
        {fam.persons.map((p, i) => (
          <PersonCard key={p.id} admin person={p} index={i} fields={fields}
            onChange={(np) => setPersons((ps) => ps.map((x, j) => (j === i ? np : x)))}
            onRemove={() => setPersons((ps) => ps.filter((_, j) => j !== i))} onCopyPrev={() => copyPrev(i)} />
        ))}
      </div>
      <button onClick={() => setPersons((ps) => [...ps, emptyPerson()])} className="mt-5 flex w-full items-center justify-center gap-2 rounded-[22px] border border-dashed border-[var(--gold)] py-5 text-[var(--gold-deep)] hover:bg-[var(--ivory-2)]" data-testid="admin-add-person-btn"><Plus size={18} /> Add person</button>

      <div className="fixed bottom-4 left-1/2 z-30 -translate-x-1/2">
        <button className="btn-ink !px-8 shadow-xl" onClick={save} disabled={busy === "save"} data-testid="save-family-btn">{busy === "save" ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save changes</button>
      </div>
    </div>
  );
}
